import "./App.css";

import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import AppDetailsStep from "./components/app_details";
import ConsultantTypeStep from "./components/consultant_type";
import ContactDetailsStep from "./components/contact_details";
import EmbeddedTypeStep from "./components/embedded_type";
import IotTypeStep from "./components/iot_type";
import MainStep from "./components/main";
import ProjectDetailsStep from "./components/project_details";
import WebTypeCommerceDetailsStep from "./components/web_type_commerce_details";
import WebTypeCustomDetailsStep from "./components/web_type_custom_details";
import WebTypeSiteDetailsStep from "./components/web_type_site_details";
import EngineeringTypeStep from "./components/engineering_type";

function App() {
  const [step, setStep] = useState(1);
  const [isModal, setModal] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [isError, setError] = useState(false);
  const [isLoader, setLoader] = useState(false);
  const [formData, setFormData] = useState({});

  const handleRedirect = (url) => {
    window.location.href = url;
  };

  const notify = (message, type = "error") => {
    type === "error" ? toast.error(message) : toast.success(message);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    if (type === "checkbox" && name === "extra-options") {
      setFormData((prev) => {
        const currentOptions = prev[name] || [];
        if (checked) {
          return { ...prev, [name]: [...currentOptions, value] };
        } else {
          return {
            ...prev,
            [name]: currentOptions.filter((option) => option !== value),
          };
        }
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleNext = () => {
    if (step === 1 && !formData.projectType) {
      notify("Please select a project type!");
      return;
    }

    if (
      step === 2 &&
      formData.projectType === "Technology Consulting" &&
      !formData["consultant-category"]
    ) {
      toast.error("Please select a consulting category.");
      return;
    }

    if (
      step === 2 &&
      formData.projectType === "Embedded Systems" &&
      !formData["embedded-category"]
    ) {
      toast.error("Please select an embedded system category.");
      return;
    }

    if (
      step === 2 &&
      formData.projectType === "Engineering Solutions" &&
      !formData["engineering-category"]
    ) {
      toast.error("Please select an engineering category.");
      return;
    }

    if (
      step === 2 &&
      formData.projectType === "Internet of Things" &&
      !formData["iot-category"]
    ) {
      toast.error("Please select an IoT category.");
      return;
    }

    if (step === 2 && formData.projectType === "Website") {
      if (
        !formData["web-infrastructure"] ||
        !formData["design-level"] ||
        !formData["site-type"]
      ) {
        toast.error("Please fill in the required * fields.");
        return;
      }
    }

    if (step === 2 && formData.projectType === "E-Commerce") {
      if (
        !formData["ecommerce-infrastructure"] ||
        !formData["design-level"] ||
        !formData["ecommerce-site-type"]
      ) {
        toast.error("Please fill in the required * fields.");
        return;
      }
    }

    if (step === 2 && formData.projectType === "Custom Web Software") {
      if (!formData["custom-software-sector"]) {
        toast.error("Please select a sector or industry.");
        return;
      }
    }

    if (
      step === 2 &&
      formData.projectType === "Mobile Application" &&
      (!formData["app-similarity"] ||
        !formData["app-platform"] ||
        (!formData.ios && !formData.android))
    ) {
      toast.error("Please fill in the required * fields.");
      return;
    }

    if (step === 3) {
      const url = formData["example-website"];
      if (url && !/^(http|https):\/\/.+$/.test(url)) {
        notify("Please enter a URL starting with 'http://' or 'https://'!");
        return;
      }

      if (!formData["project-details"] || !formData.budget) {
        notify("Please fill in all required * fields!");
        return;
      }
    }

    setStep((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === 4) {
      if (
        !formData["first-name"] ||
        !formData["last-name"] ||
        !formData["email"]
      ) {
        notify("Please fill in all required * fields.");
        return;
      }

      if (!formData["accept-privacy"]) {
        notify("Please accept the privacy policy.");
        return;
      }

      const emailRegex = /\S+@\S+\.\S+/;
      if (!emailRegex.test(formData["email"])) {
        notify("Invalid email address.");
        return;
      }

      if (formData["contact-phone"]) {
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;
        if (!phoneRegex.test(formData["contact-phone"])) {
          notify(
            "Invalid phone number. Please enter it in the international format (e.g., +905551234567)."
          );
          return;
        }
      }
    } else {
      return;
    }

    setLoader(true);
    const formDataToSend = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        formDataToSend.append(key, value.join(", "));
      } else if (value instanceof File) {
        formDataToSend.append(key, value);
      } else {
        formDataToSend.append(key, value);
      }
    });

    try {
      const response = await fetch(
        "https://onexcloud.net/send-forms/en-project-send.php",
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      if (!response.ok) {
        setLoader(false);
        setError(true);
        throw new Error("Network response was unsuccessful.");
      }

      const data = await response.json();
      if (data.status === "success") {
        notify("Form successfully submitted.", "success");
        setLoader(false);
        setSuccess(true);
      } else {
        notify("An unexpected error occurred.");
        setLoader(false);
        setError(true);
      }
    } catch (error) {
      console.error("Error:", error.message);
      notify("An unexpected error occurred.");
      setLoader(false);
      setError(true);
    }
  };

  const StepComponent = () => {
    switch (step) {
      case 1:
        return <MainStep handleChange={handleChange} formData={formData} />;
      case 2:
        switch (formData.projectType) {
          case "Website":
            return (
              <WebTypeSiteDetailsStep
                handleChange={handleChange}
                formData={formData}
              />
            );
          case "E-Commerce":
            return (
              <WebTypeCommerceDetailsStep
                handleChange={handleChange}
                formData={formData}
              />
            );
          case "Custom Web Software":
            return (
              <WebTypeCustomDetailsStep
                handleChange={handleChange}
                formData={formData}
              />
            );
          case "Mobile Application":
            return (
              <AppDetailsStep handleChange={handleChange} formData={formData} />
            );
          case "Internet of Things":
            return (
              <IotTypeStep handleChange={handleChange} formData={formData} />
            );
          case "Embedded Systems":
            return (
              <EmbeddedTypeStep
                handleChange={handleChange}
                formData={formData}
              />
            );
          case "Engineering Solutions":
            return (
              <EngineeringTypeStep
                handleChange={handleChange}
                formData={formData}
              />
            );
          case "Technology Consulting":
            return (
              <ConsultantTypeStep
                handleChange={handleChange}
                formData={formData}
              />
            );
          default:
            return null;
        }
      case 3:
        return (
          <ProjectDetailsStep
            handleChange={handleChange}
            formData={formData}
            notify={notify}
          />
        );
      case 4:
        return (
          <ContactDetailsStep handleChange={handleChange} formData={formData} />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ToastContainer theme="light" />

      <header
        className="navbar navbar-expand-lg bg-body navbar-sticky sticky-top z-fixed px-0 is-stuck"
        data-sticky-element=""
      >
        <div className="container">
          <a
            className="navbar-brand py-1 py-md-2 py-xl-1 me-2 me-sm-n4 me-md-n5 me-lg-0"
            href="https://onexyazilim.com/en/"
            target="_blank"
          >
            <span className="d-none d-sm-flex flex-shrink-0 text-primary rtl-flip me-3">
              <img
                className="pp_logo"
                src="https://onexyazilim.com/onex_yazilim_header_logo.png"
              ></img>
            </span>
            <div className="head_title_area">
              <h1 className="h2 mb-1">Project Request Form.</h1>
              <p className="mb-0 mt-0">
                Let's bring a great idea to life together!
              </p>
            </div>
          </a>
          <div className="d-flex gap-sm-1">
            <div className="dropdown">
              <button
                onClick={() => setModal(true)}
                className="theme-switcher btn btn-icon btn-dark btn-sm fs-lg border-50 animate-scale"
              >
                <span className="theme-icon-active d-flex animate-target">
                  <i className="fi-close"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="content-wrapper">
        <div className="container pt-5 pb-5 form___area">
          <form onSubmit={handleSubmit}>
            {StepComponent()}
            <footer className="sticky-bottom bg-body pb-3 bottom-buttons">
              <div
                className="progress rounded-0"
                role="progressbar"
                aria-label="Dark example"
                aria-valuenow={
                  step === 1 ? 25 : step === 2 ? 50 : step === 3 ? 75 : 100
                }
                aria-valuemin="0"
                aria-valuemax="100"
                style={{ height: "4px" }}
              >
                <div
                  className="progress-bar bg-dark d-none-dark"
                  style={{
                    width:
                      step === 1
                        ? "25%"
                        : step === 2
                        ? "50%"
                        : step === 3
                        ? "75%"
                        : "100%",
                  }}
                ></div>
                <div
                  className="progress-bar bg-light d-none d-block-dark"
                  style={{
                    width:
                      step === 1
                        ? "25%"
                        : step === 2
                        ? "50%"
                        : step === 3
                        ? "75%"
                        : "100%",
                  }}
                ></div>
              </div>
              <div className="container d-flex gap-3 pt-3">
                {step > 1 && (
                  <button
                    className="btn btn-outline-dark animate-slide-start"
                    onClick={handleBack}
                    disabled={isLoader || isSuccess || isError}
                  >
                    <i className="fi-arrow-left animate-target fs-base ms-n1 me-2"></i>
                    Back
                  </button>
                )}
                {step < 4 && (
                  <button
                    className="btn btn-dark animate-slide-end ms-auto"
                    onClick={handleNext}
                    disabled={isLoader || isSuccess || isError}
                  >
                    Next
                    <i className="fi-arrow-right animate-target fs-base ms-2 me-n1"></i>
                  </button>
                )}
                {step === 4 && (
                  <button
                    className="btn btn-dark animate-slide-end ms-auto"
                    type="submit"
                    disabled={isLoader || isSuccess || isError}
                  >
                    Submit
                    <i className="fi-arrow-right animate-target fs-base ms-2 me-n1"></i>
                  </button>
                )}
              </div>
            </footer>
          </form>
        </div>

        {isLoader && (
          <div className="loader__area">
            <div style={{ textAlign: "center", padding: "0 22px" }}>
              <div
                className="spinner-border"
                style={{ width: "4rem", height: "4rem" }}
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p
                style={{
                  fontWeight: "600",
                  textAlign: "center",
                  width: "100%",
                  marginTop: "15px",
                  marginBottom: "0",
                  fontSize: "14px",
                }}
              >
                Please wait..
              </p>
              <p
                style={{
                  textAlign: "center",
                  width: "100%",
                  marginTop: "5px",
                  marginBottom: "0",
                  fontSize: "13px",
                }}
              >
                Your information is being processed securely; this may take a
                few seconds. Thank you for your understanding!
              </p>
            </div>
          </div>
        )}
        {isSuccess && (
          <div className="result__area is__sucess">
            <div className="row justify-content-center">
              <div className="col-sm-11 col-md-9 col-lg-7 col-xl-6">
                <div className="result__icon">
                  <img src="/assets/img/done.png" alt="" />
                </div>
                <h1 className="h2 text-center pt-4 pt-md-5 mt-1 mt-sm-2 mt-md-0 mb-2">
                  Thank You! 🎉
                </h1>
                <p>
                  Your form has been successfully submitted. We will contact you
                  as soon as possible! 🙏
                </p>
                <a
                  href="https://onexyazilim.com/en/"
                  className="btn btn-lg btn-dark"
                >
                  Go to Homepage
                </a>
              </div>
            </div>
          </div>
        )}
        {isError && (
          <div className="result__area is__sucess">
            <div className="row justify-content-center">
              <div className="col-sm-11 col-md-9 col-lg-7 col-xl-6">
                <div className="result__icon">
                  <img src="/assets/img/error.png" alt="" />
                </div>
                <h1 className="h2 text-center pt-4 pt-md-5 mt-1 mt-sm-2 mt-md-0 mb-2">
                  An Error Occurred! 😔
                </h1>
                <p>
                  Your form could not be submitted. Please try again or contact
                  us directly. 🙏
                </p>

                <div
                  className="d-flex flex-nowrap gap-3"
                  style={{ justifyContent: "center" }}
                >
                  <button
                    onClick={() => window.location.reload()}
                    className="btn btn-lg btn-outline-secondary"
                  >
                    Try Again
                  </button>
                  <a
                    href="https://onexyazilim.com/en/contact"
                    className="btn btn-lg btn-dark"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <div className="modal__area">
        <div
          className={`modal-backdrop fade ${isModal && " show"}`}
          style={{ display: isModal ? "block" : "none" }}
          onClick={() => setModal(false)}
        ></div>
        <div
          className={`modal fade ${isModal && " show"}`}
          tabIndex="-1"
          role="dialog"
          style={{ display: isModal ? "block" : "none" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cancel Operation!</h5>
                <button
                  className="btn-close"
                  type="button"
                  onClick={() => setModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to cancel this operation?</p>
              </div>
              <div className="modal-footer flex-column flex-sm-row align-items-stretch">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setModal(false)}
                >
                  No, I will continue.
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setModal(false);
                    handleRedirect("https://onexyazilim.com/en/");
                  }}
                >
                  Yes, cancel!
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;
